import React, { useEffect, useState, useContext } from "react";
import { AuthContext } from "../AuthContext";
import { useNavigate } from "react-router-dom";
import { FaSave } from "react-icons/fa";

import {
  fetchUsers,
  updateUser,
  resetUserPassword,
  fetchCareHomes,
  registerUser,
} from "../utils/api";

import Notification from "./Notification";
import styles from "./UserList.module.css";

const buildUsername = (first, last) => {
  const f = first.trim().toLowerCase().slice(0, 3);
  const l = last.trim().toLowerCase().slice(0, 3);
  return f + l;
};

const UserRow = ({ usr, onEdit, onResetPassword, careHomes, loggedInUserFullName }) => {
  const usrFullName = `${usr.first_name} ${usr.surname}`.trim();

  const canResetPassword =
    loggedInUserFullName === "Robert Hall" || usrFullName !== "Robert Hall";

  return (
    <tr>
      <td>
        <input
          type="text"
          value={usr.username}
          onChange={(e) => onEdit(usr.id, "username", e.target.value)}
        />
      </td>
      <td>
        <input
          type="text"
          value={usr.first_name}
          onChange={(e) => onEdit(usr.id, "first_name", e.target.value)}
        />
      </td>
      <td>
        <input
          type="text"
          value={usr.surname}
          onChange={(e) => onEdit(usr.id, "surname", e.target.value)}
        />
      </td>
      <td>
        <select
          value={usr.role}
          onChange={(e) => onEdit(usr.id, "role", e.target.value)}
        >
          <option value="staff">Staff</option>
          <option value="manager">Manager</option>
          <option value="group_manager">Group Manager</option>
        </select>
      </td>
      <td>
        <select
          value={usr.carehome}
          onChange={(e) => onEdit(usr.id, "carehome", e.target.value)}
        >
          <option value="">-- Select a Care Home --</option>
          {careHomes.map((ch) => (
            <option key={ch.id} value={ch.home_name}>
              {ch.home_name}
            </option>
          ))}
        </select>
      </td>
      <td>
        {canResetPassword && (
          <button className={styles['reset-btn']} onClick={() => onResetPassword(usr.id)}>
            🔄 Reset Password
          </button>
        )}
      </td>
    </tr>
  );
};


const CreateUserModal = ({
  userRole,
  careHomes,
  newUser,
  setNewUser,
  onClose,
  onCreate,
}) => (
  <div className={styles['modal-overlay']}>
    <div className={styles['modal-content']}>
      <h3>Create New User</h3>
      <div className={styles['modal-field']}>
        <label>First Name</label>
        <input
          type="text"
          value={newUser.firstName}
          onChange={(e) =>
            setNewUser({ ...newUser, firstName: e.target.value })
          }
        />
      </div>
      <div className={styles['modal-field']}>
        <label>Surname</label>
        <input
          type="text"
          value={newUser.surname}
          onChange={(e) =>
            setNewUser({ ...newUser, surname: e.target.value })
          }
        />
      </div>
      <div className={styles['modal-field']}>
        <label>Role</label>
        <select
          value={newUser.role}
          onChange={(e) =>
            setNewUser({ ...newUser, role: e.target.value })
          }
        >
          <option value="staff">Staff</option>
          <option value="manager">Manager</option>
          <option value="group_manager">Group Manager</option>
        </select>
      </div>
      {userRole === "group_manager" ? (
        <div className={styles['modal-field']}>
          <label>Care Home</label>
          <select
            value={newUser.careHome}
            onChange={(e) =>
              setNewUser({ ...newUser, careHome: e.target.value })
            }
          >
            <option value="">-- Select a Care Home --</option>
            {careHomes.map((ch) => (
              <option key={ch.id} value={ch.home_name}>
                {ch.home_name}
              </option>
            ))}
          </select>
        </div>
      ) : (
        <div className={styles['modal-field']}>
          <label>Care Home</label>
          <input
            type="text"
            disabled
            value={
              userRole === "manager" ? newUser.managerCareHome : ""
            }
          />
        </div>
      )}
      <div className={styles['modal-buttons']}>
        <button className={styles['create-button']} onClick={onCreate}>
          Create
        </button>
        <button className={styles['cancel-button']} onClick={onClose}>
          Cancel
        </button>
      </div>
    </div>
  </div>
);

const UserList = () => {
  const { user, token } = useContext(AuthContext);
  const navigate = useNavigate();
  console.log(user.fullname);

  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editedUsers, setEditedUsers] = useState({});
  const [careHomes, setCareHomes] = useState([]);
  const [searchName, setSearchName] = useState("");
  const [filterHome, setFilterHome] = useState("");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newUser, setNewUser] = useState({
    firstName: "",
    surname: "",
    role: "staff",
    careHome: "",
    managerCareHome: user?.carehome || "",
  });
  const [notification, setNotification] = useState({ message: "", type: "" });

  useEffect(() => {
    if (!user || (user.role !== "manager" && user.role !== "group_manager")) {
      navigate("/dashboard");
      return;
    }
    loadUsers();
  }, [user, token, navigate]);

  useEffect(() => {
    const loadCareHomes = async () => {
      try {
        const data = await fetchCareHomes(token);
        setCareHomes(data);
      } catch (error) {
        setNotification({ message: "Failed to load care homes.", type: "error" });
      }
    };
    loadCareHomes();
  }, [token]);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const data = await fetchUsers(token);
      setAllUsers(data || []);
    } catch (error) {
      setNotification({ message: "Failed to load users. Please try again.", type: "error" });
    }
    setLoading(false);
  };

  const getFilteredUsers = () =>
    allUsers.filter((u) => {
      if (searchName.trim()) {
        const fullName = (u.first_name + " " + u.surname).toLowerCase();
        const userName = u.username.toLowerCase();
        const search = searchName.toLowerCase();
        if (!fullName.includes(search) && !userName.includes(search)) return false;
      }
      if (user.role === "manager" && u.carehome !== user.carehome) return false;
      if (user.role === "group_manager" && filterHome && u.carehome !== filterHome) return false;
      return true;
    });

  const handleEdit = (userId, field, value) => {
    setAllUsers((prev) =>
      prev.map((usr) =>
        usr.id === userId ? { ...usr, [field]: value, name: field.includes("first_name") || field.includes("surname") ? `${usr.first_name} ${usr.surname}` : usr.name } : usr
      )
    );
    setEditedUsers((prev) => {
      const prevEdits = prev[userId] || {};
      const updates = { ...prevEdits, [field]: value };
      if (field === "first_name" || field === "surname") {
        updates.name = `${updates.first_name || ''} ${updates.surname || ''}`.trim();
      }
      return { ...prev, [userId]: updates };
    });
  };

  const saveChanges = async () => {
    if (!Object.keys(editedUsers).length) {
      setNotification({ message: "No changes made.", type: "error" });
      return;
    }
    try {
      for (const userId in editedUsers) {
        const orig = allUsers.find((u) => u.id === +userId);
        const edits = editedUsers[userId];
        const payload = {
          username: edits.username || orig.username,
          first_name: edits.first_name || orig.first_name,
          surname: edits.surname || orig.surname,
          name: `${edits.first_name || orig.first_name} ${edits.surname || orig.surname}`,
          role: edits.role || orig.role,
          carehome: edits.carehome || orig.carehome,
        };
        await updateUser(userId, payload, token);
      }
      setNotification({ message: "Changes saved successfully!", type: "success" });
      setEditedUsers({});
      loadUsers();
    } catch (error) {
      setNotification({ message: "Failed to save changes.", type: "error" });
    }
  };

  const handleResetPassword = async (userId) => {
    if (!window.confirm("Are you sure you want to reset this user's password?")) return;
    try {
      await resetUserPassword(userId, token);
      setNotification({ message: "Password reset successfully.", type: "success" });
    } catch {
      setNotification({ message: "Failed to reset password.", type: "error" });
    }
  };

  const handleOpenCreateModal = () => {
    setNewUser({ firstName: "", surname: "", role: "staff", careHome: "", managerCareHome: user.role === "manager" ? user.carehome : "" });
    setShowCreateModal(true);
  };

  const handleCreateUser = async () => {
    if (!newUser.firstName.trim() || !newUser.surname.trim()) {
      setNotification({ message: "Please enter both first name and surname.", type: "error" });
      return;
    }
    const username = buildUsername(newUser.firstName, newUser.surname);
    const carehomeValue = user.role === "manager" ? user.carehome : newUser.careHome;
    const newUserObj = { username, password: "password123", role: newUser.role, first_name: newUser.firstName.trim(), surname: newUser.surname.trim(), name: `${newUser.firstName.trim()} ${newUser.surname.trim()}`, carehome: carehomeValue };
    try {
      await registerUser(newUserObj, token);
      setNotification({ message: "User created successfully!", type: "success" });
      setShowCreateModal(false);
      loadUsers();
    } catch {
      setNotification({ message: "Failed to create user.", type: "error" });
    }
  };

  const filteredUsers = getFilteredUsers();

  return (
    <div className={styles['user-list-container']}>
      <h2>Manage Users</h2>
      {loading && <p className={styles['loading-message']}>Loading...</p>}
      {notification.message && (
        <Notification
          message={notification.message}
          type={notification.type}
          onClose={() => setNotification({ message: "", type: "" })}
        />
      )}
      <div className={styles['filters-row']}>
        <div className={styles['filters-left']}>
          <input
            type="text"
            className={styles['filter-input']}
            placeholder="Search by name or username..."
            value={searchName}
            onChange={(e) => setSearchName(e.target.value)}
          />
          {user.role === "group_manager" && (
            <select
              className={styles['filter-select']}
              value={filterHome}
              onChange={(e) => setFilterHome(e.target.value)}
            >
              <option value="">-- All Care Homes --</option>
              {careHomes.map((ch) => (
                <option key={ch.id} value={ch.home_name}>
                  {ch.home_name}
                </option>
              ))}
            </select>
          )}
        </div>
        <div className={styles['button-group']}>
          <button className={styles['create-user-btn']} onClick={handleOpenCreateModal}>
            + Create User
          </button>
          <button className={styles['save-btn']} onClick={saveChanges}>
            <FaSave /> Save Changes
          </button>
        </div>
      </div>
      <div className={styles['userlist-table-wrapper-UL']}>
        <table className={styles['user-table']}>
          <thead>
            <tr>
              <th>Username</th>
              <th>First Name</th>
              <th>Surname</th>
              <th>Role</th>
              <th>Care Home</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.length > 0 ? (
              filteredUsers.map((usr) => (
                <UserRow
                  key={usr.id}
                  usr={usr}
                  onEdit={handleEdit}
                  onResetPassword={handleResetPassword}
                  careHomes={careHomes}
                  loggedInUserFullName={user.fullname}
                />
              ))
            ) : (
              <tr>
                <td colSpan="6">No users found.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {showCreateModal && (
        <CreateUserModal
          userRole={user.role}
          careHomes={careHomes}
          newUser={newUser}
          setNewUser={setNewUser}
          onClose={() => setShowCreateModal(false)}
          onCreate={handleCreateUser}
        />
      )}
    </div>
  );
};

export default UserList;